import React, { useEffect, useMemo, useCallback } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { styles } from "./style";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import * as Router from "react-router-dom";
import { MenuItem } from "./menu-item";
import * as paths from "routes/paths";
import * as Icons from "react-feather";
import { SidebarMenuItem } from "./sidebar-menu-item";
import { User } from "model/user";
import { activeChange } from "redux/slice/sidebar-menu";
import { ViewType } from "type/view-type";
import * as env from "shared/env";
import { getActive, getIntegrationByTypeId } from "redux/selector";
import * as credentials from "shared/credentials";
import { KestrelVersion } from "type/kestrel-version";
import { canShowSupplementalListings } from "redux/selector";
import { displayPermitted } from "component/page/settings-display/access";
import { idxPermitted } from "component/page/settings-idx/access";
import { mlsSetupMovesToTab } from "component/page/featured-mls-ids/access";
import { IntegrationType } from "type/integration-type";

interface Props extends
	Router.RouteComponentProps,
	WithStyles<typeof styles>
{
	user: User;
	viewType: ViewType;
}

const Component: React.FC<Props> = ({ user, viewType, classes = {}, match }) => {
	const active = useSelector(getActive);
	const canShowSupplementalListingsProp = useSelector(canShowSupplementalListings);
	const wixIntegration = useSelector((state: any) => getIntegrationByTypeId(state, IntegrationType.WIX_APP));
	const dispatch = useAppThunkDispatch();

	const recursivelyGenerateIds = useCallback((items: MenuItem[], parentId: string) => {
		items.forEach((item, index) => {
			item.id = (parentId ? parentId + "." : "") + index;
			if (item.children) {
				recursivelyGenerateIds(item.children, item.id);
			}
		});
	}, []);

	const recursivelyFindActiveItem = useCallback((items: MenuItem[]): MenuItem | undefined => {
		const path = match.path;
		for (let i = 0; i < items.length; i++) {
			const item = items[i];
			if (item.path === path) {
				return item;
			}
			if (item.children) {
				const result = recursivelyFindActiveItem(item.children);
				if (result) {
					return result;
				}
			}
		}
	}, [match.path]);

	const items = useMemo(() => {
		const showSupplementalListings = user.permissions.supplementalListings && canShowSupplementalListingsProp;
		const maxForTeams = user.permissions.broker && user.permissions.crm && user.permissions.showTeamFeatures;
		let menuItems: MenuItem[] = [];
		if (!user.permissions.oneCrm) {
			menuItems = [
				(user.permissions.leads) && {
					label: "Leads",
					className: "ihf-leads",
					icon: Icons.User,
					children: [
						{
							path: paths.lead,
						},
						user.permissions.crm && {
							label: "Dashboard",
							className: "ihf-leaddashboard",
							path: paths.leadDashboard,
						},
						user.permissions.crm && {
							label: "Lead List",
							className: "ihf-searchleads",
							path: paths.leads,
						},
						((user.admin && user.permissions.crmTasks) || (user.permissions.broker && user.permissions.crm && user.permissions.showTeamFeatures)) && {
							label: "Tasks",
							path: paths.tasks,
						},				
						{
							label: "Lead Activity",
							className: "ihf-leadactivity",
							path: paths.leadActivity,
						},
						!user.permissions.crm && {
							label: "Lead List",
							className: "ihf-searchleads",
							path: paths.leads,
						},
						user.admin && {
							label: "Export Leads",
							className: "ihf-leadexport",
							path: paths.leadExport,
						},
						user.admin && user.reseller.direct && {
							label: "Coaching & Support",
							className: "ihf-supportCenter",
							path: paths.supportCenter,
						},
					],
				},
				user.admin && user.permissions.markets && {
					label: "Markets",
					icon: Icons.MapPin,
					children: [
						{
							path: paths.market,
						},
						{
							label: "Markets",
							className: "ihf-savedsearchpages",
							path: paths.markets,
						},
						{
							label: "Add New Market",
							className: "ihf-savedsearchpages",
							path: paths.marketCreate,
						},
						user.permissions.report && user.permissions.version === 9 && {
							label: "Market Settings",
							className: "ihf-savedsearchpages",
							path: paths.marketSettings,
						},
						user.reseller.direct && {
							label: "Coaching & Support",
							className: "ihf-supportCenter",
							path: paths.supportCenter,
						},
					],
				},
				(user.admin || user.permissions.modifySubscriptions) && {
					label: "Listings",
					icon: Icons.Home,
					children: [
						{
							label: "Active",
							className: "ihf-activelistings",
							path: paths.listingsActive,
						},
						user.admin && user.permissions.soldPendingListings && {
							label: "Sold/Off-Market",
							className: "ihf-soldpending",
							path: paths.listingsOffMarket,
						},
						user.admin && showSupplementalListings && {
							label: "Supplemental",
							className: "ihf-supplemental",
							path: paths.listingsSupplemental,
						},
						user.admin && user.permissions.markets && {
							label: "Markets (Saved Searches)",
							className: "ihf-savedsearchpages",
							path: paths.markets,
						},
						user.admin && user.reseller.direct && {
							label: "Coaching & Support",
							className: "ihf-supportCenter",
							path: paths.supportCenter,
						},
					],
				},
				user.admin && user.permissions.reporting && {
					label: "Reports",
					icon: Icons.BarChart2,
					children: [
						{
							label: "Reports Overview",
							className: "ihf-reportsoverview",
							path: paths.reportsOverview,
						},
						{
							label: "Email Activity",
							className: "ihf-emailactivity",
							path: paths.reportsEmailActivity,
						},
						{
							label: "Organizer Activity",
							className: "ihf-organizeractivity",
							children: [
								{
									label: "Saved Searches",
									className: "ihf-emailactivity-savedsearches",
									path: paths.reportsSavedSearches,
								},
								{
									label: "Saved Properties",
									className: "ihf-emailactivity-savedprops",
									path: paths.reportSavedListings,
								},
							],
						},
						{
							label: "Leads & Contacts",
							className: "ihf-leadscontacts",
							path: paths.reportsLeads,
						},
						{
							label: "Mobile Statistics",
							className: "ihf-mobilestats",
							path: paths.reportsMobile,
						},
						user.reseller.direct && {
							label: "Coaching & Support",
							className: "ihf-supportCenter",
							path: paths.supportCenter,
						},
					],
				},
				{
					label: "Setup",
					icon: Icons.Settings,
					children: [
						// MAX-4419 flattens the old "Website" group (and v9's Version / WordPress Plugin) into
						// IDX and Display, which lead the Setup nav. SEO Pages moves into IDX, CSS into
						// Display, and the WordPress Plugin page's registration key into IDX's Connection block.
						idxPermitted(user) && {
							label: "IDX",
							className: "ihf-idx",
							path: paths.settingsIdx,
						},
						displayPermitted(user) && {
							label: "Display",
							className: "ihf-display",
							path: paths.settingsDisplay,
						},
						user.admin && user.permissions.kestrelAll && user.kestrelVersion.all && !user.websitePlatform.wordpress && !user.websitePlatform.wix && {
							label: "Widgets",
							className: "widgets",
							path: paths.settingsWidgets,
						},
						// MAX-4139 moves Profile to the top-right profile menu and removes the "Account"
						// parent, promoting these to sit directly under Setup. Defined once so the two
						// shapes cannot drift while the flag is still in place.
						...(() => {
							const accountChildren = [
								((user.admin && wixIntegration)) && {
									label: "Overview",
									className: "ihf-setupoverview",
									path: paths.settingsOverview,
								},
								user.admin && user.permissions.standalone && !user.kestrelVersion.all && {
									label: "IDX Page Links",
									className: "ihf-siteLinks",
									path: paths.settingsPageLinks,
								},
								user.admin && {
									label: "Account",
									className: "ihf-myaccount",
									path: paths.settingsAccount,
								},
								// Profile only appears in the sidebar pre-refresh; afterwards it lives
								// in the profile menu.
								(!env.refreshProfile && user.agentId) && {
									label: "My Profile",
									className: "ihf-myaccount",
									path: paths.settingsProfile,
								},
								(user.admin && !user.agentId) && {
									label: "Change Password",
									className: "ihf-changepassword",
									path: paths.settingsPassword,
								},
								!env.billingRefresh && user.admin && user.permissions.billing && {
									label: "Billing Information",
									className: "ihf-billinginfo",
									path: paths.settingsBilling,
								},
								// With the MLS Setup redesign on, these users have the MLS IDs tab instead.
								user.admin && !mlsSetupMovesToTab(user) && {
									label: "MLS Setup",
									className: "ihf-mlssetup",
									path: paths.settingsMls,
								},
								user.admin && user.permissions.version >= 9 && {
									label: "Integrations",
									className: "ihf-integrations",
									path: paths.settingsIntegrations,
								},
							];
							return env.refreshProfile
								? accountChildren
								: [{ label: "Account", className: "ihf-account", children: accountChildren }];
						})(),
						user.admin && (user.permissions.standalone && user.kestrelVersion !== KestrelVersion.ALL) && {
							label: "Design & Branding",
							className: "ihf-designbranding",
							children: [
								{
									label: "Branding Setup",
									className: "ihf-brandingsetup",
									path: paths.settingsBranding,
								},
								{
									label: "Templates",
									className: "ihf-templates",
									path: paths.settingsTemplates,
								},
								user.permissions.sidebarWidgets && {
									label: "Widgets",
									className: "ihf-widgets",
									path: paths.settingsWidgets,
								},
								{
									label: "Framing Setup",
									className: "ihf-framing",
									path: paths.settingsFraming,
								},
								{
									label: "Sub-Heads",
									className: "ihf-subheads",
									path: paths.settingsSubHeads,
								},
								user.permissions.version <= 8 && {
									label: "Page-Specific HTML",
									className: "ihf-pagehtml",
									path: paths.settingsPageSpecificBranding,
								},
							],
						},
						user.admin && {
							label: env.notificationsRefresh ? "Notifications" : "Alert & Email Settings",
							className: "ihf-emailsettings",
							children: [
								// The refresh puts Lead Activity above Emails; legacy order is preserved when off.
								...(env.notificationsRefresh
									? [
										{
											label: "Lead Activity",
											className: "ihf-emailadvrouting",
											path: paths.settingsEmailRouting,
										},
										{
											label: "Emails",
											className: "ihf-emailbasicsetup",
											path: paths.settingsEmail,
										},
									]
									: [
										{
											label: "Basic Setup",
											className: "ihf-emailbasicsetup",
											path: paths.settingsEmail,
										},
										{
											label: "Advanced Routing",
											className: "ihf-emailadvrouting",
											path: paths.settingsEmailRouting,
										},
									]),
								(!user.permissions.plugin || (user.websitePlatform && !user.websitePlatform.wordpress)) && {
									label: "Branding",
									className: "ihf-emailbranding",
									path: paths.settingsEmailBranding,
								},
								user.permissions.version <= 8 && {
									label: "Customization",
									className: "ihf-emailcustomization",
									path: paths.settingsEmailCustomization,
								},
								user.permissions.version <= 8 && {
									label: "Geographic Routing",
									className: "ihf-georouting",
									path: paths.settingsEmailGeographicRouting,
								},
							],
						},
						user.admin && (user.permissions.listingSearch || user.permissions.listingMapSearch) && {
							label: "Search",
							className: "ihf-searchsettings",
							children: [
								user.kestrelVersion.all && {
									label: "General",
									className: "ihf-searchgeneral",
									path: paths.settingsSearchGeneral,
								},
								{
									label: "Property Types",
									className: "ihf-propertytypesprices",
									path: paths.settingsPropertyTypes,
								},
								// MAX-3820: when the CF re-platform flag is ON, "Location Customization" +
								// "Search Cities" are consolidated into a single "Locations" item. When OFF,
								// keep the two legacy items for backwards compatibility.
								...(env.coldFusionReplatform
									? [{
										label: "Locations",
										className: "ihf-searchareas",
										path: paths.settingsLocations,
									}]
									: [{
										label: "Location Customization",
										className: "ihf-searchareas",
										path: paths.settingsLocationCustomization,
									}, {
										label: "Search Cities",
										className: "ihf-searchcities",
										path: paths.settingsSearchCities,
									}]),
								user.permissions.neighborhoods && {
									label: "Search Neighborhoods",
									className: "ihf-searchneighborhoods",
									path: paths.settingsSearchNeighborhoods,
								},
								{
									label: "Open Houses",
									className: "ihf-openhomesettings",
									path: paths.settingsOpenHouse,
								},
								(user.permissions.listingMapSearch || user.permissions.listingPolygonSearch) && {
									label: env.refreshDesignsDev ? "Map" : "Map Search",
									className: "ihf-mapsearch",
									path: paths.settingsMapSearch,
								},
							],
						},
						user.admin && (user.permissions.listingResults || user.permissions.listingDetail) && {
							label: "Listings",
							className: "ihf-listingsettings",
							children: [
								user.permissions.listingResults && {
									label: "Featured Listings",
									className: "ihf-resultsfeatured",
									path: paths.settingsListingResults,
								},
								user.permissions.listingDetail && {
									label: "Listing Details",
									className: "ihf-propertydetails",
									path: paths.settingsListingDetails,
								},
								user.permissions.listingDetail && {
									label: env.refreshDesignsDev ? "Mortgage Calculator" : "Mortgage Calc Settings",
									path: paths.settingsMortgageCalculator,
								}
							],
						},
						user.admin && user.permissions.leads && {
							label: "Lead Capture",
							className: "ihf-leadcapture",
							children: [
								user.permissions.leadUserRegistration && !user.permissions.leadForceUserRegistration && {
									label: "Lead Registration",
									className: "ihf-userregistration",
									path: paths.settingsUserRegistration,
								},
								{
									label: "Capture Forms",
									className: "ihf-contactforms",
									path: paths.settingsContactForms,
								},
							],
						},
						!env.refreshDesignsDev && user.admin && (user.permissions.geographicRouting || user.permissions.roundRobinRouting) && {
							label: "Lead Routing",
							className: "ihf-leadrouting",
							path: paths.settingsleadRouting,
						},
						user.admin && (user.permissions.mobileSite) && {
							label:"Mobile Setup",
							className: "ihf-mobile",
							path: paths.settingsMobile,
						},
						user.admin && user.permissions.broker && {
							label: maxForTeams ? "Team & Offices" : "Agents & Offices",
							className: "ihf-agentoffices",
							children: [
								{
									label: env.refreshDesignsDev ? "General" : "Basic Settings",
									className: "ihf-brokersettings",
									path: paths.settingsBroker,
								},
								{
									label: maxForTeams ? "Team" : "Agents",
									className: "ihf-agents",
									path: paths.settingsAgents,
								},
								{
									label: "Offices",
									className: "ihf-offices",
									path: paths.settingsOffices,
								},
								env.refreshDesignsDev && user.admin && (user.permissions.geographicRouting || user.permissions.roundRobinRouting) && {
									label: "Lead Routing",
									className: "ihf-leadrouting",
									path: paths.settingsleadRouting,
								},
								{
									path: paths.settingsAgent,
								},
								{
									path: paths.settingsOffice,
								},
							],
						},
						user.admin && user.reseller.direct && {
							label: "Coaching & Support",
							className: "ihf-supportCenter",
							path: paths.supportCenter,
						},
					],
				},
				(user.admin && user.permissions.campaigns && user.permissions.blastCampaigns) && {
					label: "Communications",
					icon: Icons.Send,
					children: [
						user.permissions.blastCampaigns && {
							label: "Send Email",
							children: [
								{
									path: paths.campaignDraftEmail,
								},
								{
									path: paths.campaignSentEmail,
								},
								{
									label: "New Email",
									path: paths.campaignNewEmail,
								},
								{
									label: "Email Drafts",
									path: paths.campaignDraftEmails,
								},
								{
									label: "Sent Emails",
									path: paths.campaignSentEmails,
								},
							],
						},
						{
							label: "Marketing Automation",
							children: [
								{
									path: paths.campaign,
								},
								{
									label: "Campaigns",
									path: paths.campaigns,
								},
								{
									label: "Automations",
									path: paths.campaignsRouting,
								},
								(user.permissions.crmAnniversary) && {
									label: "Automated Greetings",
									path: paths.campaignAutomatedGreetings,
								},
								{
									label: "Settings",
									path: paths.campaignsSettings,
								},
								user.reseller.direct && {
									label: "Help",
									path: "https://kb.ihomefinder.com/s/topic/0TO1Q000000kXBUWA2/marketing-automation",
									target: "_blank",
								},
							],
						},
						(user.permissions.dripMms && !user.pending) && {
							label: "Texting",
							className: "text-messaging",
							path: paths.campaignsTexting,
						},
						user.reseller.direct && {
							label: "Coaching & Support",
							className: "ihf-supportCenter",
							path: paths.supportCenter,
						},
					],
				},
				(user.admin && user.permissions.campaigns && !user.permissions.blastCampaigns) && {
					label: "Communications",
					icon: Icons.Send,
					children: [
						{
							path: paths.campaign,
						},
						{
							label: "Campaigns",
							path: paths.campaigns,
						},
						{
							label: "Automations",
							path: paths.campaignsRouting,
						},
						(user.permissions.crmAnniversary) && {
							label: "Automated Greetings",
							path: paths.campaignAutomatedGreetings,
						},
						{
							label: "Settings",
							path: paths.campaignsSettings,
						},
						user.reseller.direct && {
							label: "Help",
							path: "https://kb.ihomefinder.com/s/topic/0TO1Q000000kXBUWA2/marketing-automation",
							target: "_blank",
						},
					],
				},
			] as MenuItem[];
		} else {
			// One CRM
			if (user.admin) {
				menuItems = [
					{
						label: "Leads",
						className: "ihf-leads",
						icon: Icons.User,
						children: [
							{
								label: "Dashboard",
								path: paths.oneBusinessDashboard,
							},
							user.permissions.crm && {
								label: "Dashboard",
								className: "ihf-leaddashboard",
								path: paths.leadDashboard,
							},
							user.permissions.crm && {
								label: "Lead List",
								className: "ihf-searchleads",
								path: paths.leads,
							},
							user.permissions.crmTasks && {
								label: "Tasks",
								path: paths.tasks,
							},
							{
								label: "Leads",
								children: [
									{
										label: "Lead List",
										path: paths.oneLeads,
									},
									{
										label: "Lead Routing",
										path: paths.oneLeadRouting,
									},
									{
										label: "Add Lead",
										path: paths.oneAddNewLead,
									},
									{
										label: "Active Prospects",
										path: paths.oneActiveProspects,
									},
									{
										label: "Deals",
										children: [
											{
												label: "Pending Deals",
												path: paths.onePendingProspects,
											},
											{
												label: "Closed Deals",
												path: paths.oneClosedProspects,
											},
										],
									},
									{
										label: "Contacts",
										path: paths.oneContactManagement,
									},
									{
										label: "List Manager",
										path: paths.oneListManager,
									},
								],
							},
						],
					},
					user.permissions.markets && {
						label: "Markets",
						icon: Icons.MapPin,
						children: [
							{
								path: paths.market,
							},
							{
								label: "Markets",
								className: "ihf-savedsearchpages",
								path: paths.markets,
							},
							{
								label: "Add New Market",
								className: "ihf-savedsearchpages",
								path: paths.marketCreate,
							},
							user.permissions.report && user.permissions.version === 9 && {
								label: "Market Settings",
								className: "ihf-savedsearchpages",
								path: paths.marketSettings,
							},
						],
					},
					{
						label: "Listings",
						icon: Icons.Home,
						children: [
							{
								label: "Active",
								className: "ihf-activelistings",
								path: paths.listingsActive,
							},
							user.permissions.soldPendingListings && {
								label: "Sold/Off-Market",
								className: "ihf-soldpending",
								path: paths.listingsOffMarket,
							},
							showSupplementalListings && {
								label: "Supplemental",
								className: "ihf-supplemental",
								path: paths.listingsSupplemental,
							},
							user.permissions.markets && {
								label: "Markets (Saved Searches)",
								className: "ihf-savedsearchpages",
								path: paths.markets,
							},
						],
					},
					user.permissions.reporting && {
						label: "Reports",
						icon: Icons.BarChart2,
						children: [
							{
								label: "Reports Overview",
								className: "ihf-reportsoverview",
								path: paths.reportsOverview,
							},
							{
								label: "Email Activity",
								className: "ihf-emailactivity",
								path: paths.reportsEmailActivity,
							},
							{
								label: "Organizer Activity",
								className: "ihf-organizeractivity",
								children: [
									{
										label: "Saved Searches",
										className: "ihf-emailactivity-savedsearches",
										path: paths.reportsSavedSearches,
									},
									{
										label: "Saved Properties",
										className: "ihf-emailactivity-savedprops",
										path: paths.reportSavedListings,
									},
								],
							},
							{
								label: "Leads & Contacts",
								className: "ihf-leadscontacts",
								path: paths.reportsLeads,
							},
							{
								label: "Mobile Statistics",
								className: "ihf-mobilestats",
								path: paths.reportsMobile,
							},
						],
					},
					{
						label: "Setup",
						icon: Icons.Settings,
						children: [
							{
								label: "IDX",
								className: "ihf-idx",
								path: paths.settingsIdx,
							},
							{
								label: "Display",
								className: "ihf-display",
								path: paths.settingsDisplay,
							},
							{
								label: "Account",
								className: "ihf-account",
								children: [
									{
										label: "Overview",
										className: "ihf-setupoverview",
										path: paths.settingsOverview,
									},
									user.permissions.standalone && !user.kestrelVersion.all && {
										label: "IDX Page Links",
										className: "ihf-siteLinks",
										path: paths.settingsPageLinks,
									},
									{
										label: "My Account",
										className: "ihf-myaccount",
										path: paths.settingsAccount,
									},
									{
										label: "Change Password",
										className: "ihf-changepassword",
										path: paths.settingsPassword,
									},
									!env.billingRefresh && user.permissions.billing && {
										label: "Billing Information",
										className: "ihf-billinginfo",
										path: paths.settingsBilling,
									},
									!mlsSetupMovesToTab(user) && {
										label: "MLS Setup",
										className: "ihf-mlssetup",
										path: paths.settingsMls,
									},
									user.permissions.version >= 9 && {
										label: "Integrations",
										className: "ihf-integrations",
										path: paths.settingsIntegrations,
									},
								],
							},
							user.permissions.standalone && {
								label: "Design & Branding",
								className: "ihf-designbranding",
								children: [
									{
										label: "Branding Setup",
										className: "ihf-brandingsetup",
										path: paths.settingsBranding,
									},
									{
										label: "Templates",
										className: "ihf-templates",
										path: paths.settingsTemplates,
									},
									user.permissions.sidebarWidgets && {
										label: "Widgets",
										className: "ihf-widgets",
										path: paths.settingsWidgets,
									},
									{
										label: "Framing Setup",
										className: "ihf-framing",
										path: paths.settingsFraming,
									},
									{
										label: "Sub-Heads",
										className: "ihf-subheads",
										path: paths.settingsSubHeads,
									},
									user.permissions.version <= 8 && {
										label: "Page-Specific HTML",
										className: "ihf-pagehtml",
										path: paths.settingsPageSpecificBranding,
									},
								],
							},
							{
								label: env.notificationsRefresh ? "Notifications" : "Alert & Email Settings",
								className: "ihf-emailsettings",
								children: [
									// The refresh puts Lead Activity above Emails; legacy order is preserved when off.
									...(env.notificationsRefresh
										? [
											{
												label: "Lead Activity",
												className: "ihf-emailadvrouting",
												path: paths.settingsEmailRouting,
											},
											{
												label: "Emails",
												className: "ihf-emailbasicsetup",
												path: paths.settingsEmail,
											},
										]
										: [
											{
												label: "Basic Setup",
												className: "ihf-emailbasicsetup",
												path: paths.settingsEmail,
											},
											{
												label: "Advanced Routing",
												className: "ihf-emailadvrouting",
												path: paths.settingsEmailRouting,
											},
										]),
									!user.permissions.plugin || (user.websitePlatform && !user.websitePlatform.wordpress) && {
										label: "Branding",
										className: "ihf-emailbranding",
										path: paths.settingsEmailBranding,
									},
									user.permissions.version <= 8 && {
										label: "Customization",
										className: "ihf-emailcustomization",
										path: paths.settingsEmailCustomization,
									},
									user.permissions.version <= 8 && {
										label: "Geographic Routing",
										className: "ihf-georouting",
										path: paths.settingsEmailGeographicRouting,
									},
								],
							},
							(user.permissions.listingSearch || user.permissions.listingMapSearch) && {
								label: "Search",
								className: "ihf-searchsettings",
								children: [
									{
										label: "Property Types",
										className: "ihf-propertytypesprices",
										path: paths.settingsPropertyTypes,
									},
									// MAX-3820: when the CF re-platform flag is ON, "Location Customization" +
									// "Search Cities" are consolidated into a single "Locations" item. When OFF,
									// keep the two legacy items for backwards compatibility.
									...(env.coldFusionReplatform
										? [{
											label: "Locations",
											className: "ihf-searchareas",
											path: paths.settingsLocations,
										}]
										: [{
											label: "Location Customization",
											className: "ihf-searchareas",
											path: paths.settingsLocationCustomization,
										}, {
											label: "Search Cities",
											className: "ihf-searchcities",
											path: paths.settingsSearchCities,
										}]),
									user.permissions.neighborhoods && {
										label: "Search Neighborhoods",
										className: "ihf-searchneighborhoods",
										path: paths.settingsSearchNeighborhoods,
									},
									{
										label: "Open Houses",
										className: "ihf-openhomesettings",
										path: paths.settingsOpenHouse,
									},
									(user.permissions.listingMapSearch || user.permissions.listingPolygonSearch) && {
										label: env.refreshDesignsDev ? "Map" : "Map Search",
										className: "ihf-mapsearch",
										path: paths.settingsMapSearch,
									},
								],
							},
							(user.permissions.listingResults || user.permissions.listingDetail) && {
								label: "Listings",
								className: "ihf-listingsettings",
								children: [
									user.permissions.listingResults && {
										label: "Featured Listings",
										className: "ihf-resultsfeatured",
										path: paths.settingsListingResults,
									},
									user.permissions.listingDetail && {
										label: "Listing Details",
										className: "ihf-propertydetails",
										path: paths.settingsListingDetails,
									},
									user.permissions.listingDetail && {
										label: env.refreshDesignsDev ? "Mortgage Calculator" : "Mortgage Calc Settings",
										path: paths.settingsMortgageCalculator,
									}
								],
							},
							user.permissions.leads && {
								label: "Lead Capture",
								className: "ihf-leadcapture",
								children: [
									user.permissions.leadUserRegistration && !user.permissions.leadForceUserRegistration && {
										label: "Lead Registration",
										className: "ihf-userregistration",
										path: paths.settingsUserRegistration,
									},
									{
										label: "Capture Forms",
										className: "ihf-contactforms",
										path: paths.settingsContactForms,
									},
								],
							},
							(user.permissions.mobileSite) && {
								label: "Mobile Setup",
								className: "ihf-mobile",
								path: paths.settingsMobile,
							},
							user.permissions.broker && {
								label: maxForTeams ? "Team & Offices" : "Agents & Offices",
								className: "ihf-agentoffices",
								children: [
									{
										label: env.refreshDesignsDev ? "General" : "Basic Settings",
										className: "ihf-brokersettings",
										path: paths.settingsBroker,
									},
									{
										label: maxForTeams ? "Team" : "Agents",
										className: "ihf-agents",
										path: paths.settingsAgents,
									},
									{
										label: "Offices",
										className: "ihf-offices",
										path: paths.settingsOffices,
									},
									{
										path: paths.settingsAgent,
									},
									{
										path: paths.settingsOffice,
									},
								],
							},
						],
					},
					user.onePermissions.sendEmail && {
						label: "Communications",
						icon: Icons.Send,
						children: [
							{
								label: "Email",
								children: [
									{
										label: "Reports",
										path: paths.oneEmailUsage,
									},
									{
										label: "Send Email",
										path: paths.oneBlastEmail,
									},
									{
										label: "Queue",
										path: paths.oneEmailQueue,
									},
								],
							},
							{
								label: "Groups",
								path: paths.oneGroups,
							},
							{
								label: "Campaigns",
								path: paths.oneCampaigns,
							},
						],
					},
					{
						label: "CRM Tools",
						icon: Icons.Sliders,
						children: [
							{
								label: "Team Management",
								path: paths.oneTeamList,
							},
							{
								label: "Business Process Settings",
								path: paths.oneBusinessProcessSettings,
							},
							{
								label: "Action Plan Templates",
								path: paths.oneActionPlanTemplateManagement,
							},
							{
								label: "Marketing Expenses",
								path: paths.oneMarketingExpenses,
							},
							{
								label: "Report Manager",
								path: paths.oneReports,
							}
						],
					},
				] as MenuItem[];
			} else if (user.type.agent) {
				menuItems = [
					{
						label: "Leads",
						className: "ihf-leads",
						icon: Icons.User,
						children: [
							{
								label: "Dashboard",
								path: paths.oneBusinessDashboard,
							},
							{
								label: "Leads",
								children: [
									{
										label: "Lead List",
										path: paths.oneLeads,
									},
									{
										label: "Add Lead",
										path: paths.oneAddNewLead,
									},
									{
										label: "Active Prospects",
										path: paths.oneActiveProspects,
									},
									{
										label: "Deals",
										children: [
											{
												label: "Pending Deals",
												path: paths.onePendingProspects,
											},
											{
												label: "Closed Deals",
												path: paths.oneClosedProspects,
											},
										],
									},
									{
										label: "Contacts",
										path: paths.oneContactManagement,
									},
									{
										label: "List Manager",
										path: paths.oneListManager,
									},
								],
							},
						],
					},
					{
						label: "Listings",
						icon: Icons.Home,
						children: [
							{
								label: "Active",
								className: "ihf-activelistings",
								path: paths.listingsActive,
							},
						],
					},
					{
						label: "IDX Setup",
						icon: Icons.Settings,
						children: [
							{
								path: paths.settingsOverview
							},
							user.permissions.modifyListings && {
								label: "Manage Listings",
								path: paths.listingsActive,
							},
							user.permissions.modifyBio && {
								label: "Modify Bio",
								path: paths.settingsAccount,
							},
							user.permissions.modifyCredentials && {
								label: "Change Password",
								path: paths.settingsPassword,
							},
							user.permissions.modifySubscriptions && {
								label: "Email Message",
								path: paths.settingsEmail,
							},
							user.permissions.modifySubscriptions && {
								label: "Manage Subscribers",
								path: paths.oneMarkets,
							},
						],
					},
					user.onePermissions.sendEmail && {
						label: "Communications",
						icon: Icons.Send,
						children: [
							{
								label: "Email",
								children: [
									{
										label: "Reports",
										path: paths.oneEmailUsage,
									},
									{
										label: "Send Email",
										path: paths.oneBlastEmail,
									},
									{
										label: "Queue",
										path: paths.oneEmailQueue,
									},
								],
							},
							{
								label: "Groups",
								path: paths.oneGroups,
							},
							{
								label: "Campaigns",
								path: paths.oneCampaigns,
							},
						],
					},
					{
						label: "CRM Tools",
						icon: Icons.Sliders,
						children: [
							{
								label: "Team Management",
								path: paths.oneTeamList,
							},
							{
								label: "Business Process Settings",
								path: paths.oneBusinessProcessSettings,
							},
							{
								label: "Action Plan Templates",
								path: paths.oneActionPlanTemplateManagement,
							},
							{
								label: "Marketing Expenses",
								path: paths.oneMarketingExpenses,
							},
							{
								label: "Report Manager",
								path: paths.oneReports,
							}
						],
					},
				] as MenuItem[];
			}
		}
		menuItems = menuItems.filter(function f(o: any) {
			if (!(o instanceof Object)) {
				return false;
			}
			if (o.children) {
				o.children = o.children.filter(f);
			}
			return true;
		});
		recursivelyGenerateIds(menuItems, "");
		return menuItems;
	}, [user, canShowSupplementalListingsProp, wixIntegration, recursivelyGenerateIds]);

	useEffect(() => {
		const item = recursivelyFindActiveItem(items);
		if (item && item.id) {
			dispatch(activeChange(item.id));
		}
	}, [items, recursivelyFindActiveItem, dispatch]);

	const displayUpgrade = user.permissions.crmUpgrade && active && (active.startsWith("0") || active.startsWith("4"));
	const creds = credentials.get();
	let upgradeUrl;
	let crmUpgradeUsername;
	let crmUpgradePassword;
	if (creds) {
		const { username, password } = creds;
		upgradeUrl = `${env.javaUrl}/account/upgrade/client/${user.clientId}`;
		crmUpgradeUsername = `${username}`;
		crmUpgradePassword = `${password}`;
	}

	return (
		<>
			{items.map((item, index) => (
				<SidebarMenuItem key={index} item={item} user={user} />
			))}
		</>
	);
};

export const SidebarMenu = withStyles(Router.withRouter(Component), styles);